import React, { useState, useRef, useEffect } from 'react';
import Lang from '../../config/lang';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import ViewConfig from '../../config/viewConfig';
import Swal from 'sweetalert2';
import { addData } from '../../config/data';
import { loadCurrentApplicantCompletion, loadCurrentApplicantProfile } from '../../config/applicantApi';
import { useDrawerTransition } from "../../context/drawerProvider";
import { clearSession, isSessionExpired } from '../../config/helper';
import { setIsLogin } from '../../redux/actions/language';

const AccordionItem = ({ title, tags, content, isOpen, onClick, id }) => {
    const location = useLocation();
    const { lang } = useSelector((state) => state.globalReducer);
    const contentRef = useRef(null);
    const [height, setHeight] = useState('0px');
    const mobileView = ViewConfig();

    useEffect(() => {
        if (isOpen) {
            setHeight(`${contentRef.current.scrollHeight}px`);
        } else {
            setHeight('0px');
        }
        const isPortal = location.pathname.includes(`/${lang}/portal`);
        if (isPortal) {
            if (id) {
                if (isOpen) {
                    const encodedID = btoa(id);
                    window.history.replaceState({}, "", `/${lang}/portal?id=${encodedID}`);
                } else {
                    window.history.replaceState({}, "", `/${lang}/portal`);
                }
            }
        }

    }, [isOpen]);

    return (
        <div className={`bg-white mb-5 rounded-2xl md:rounded-[35px]` + (isOpen ? 'border border-[#034694]' : '')} style={{ boxShadow: isOpen ? '0px 0px 1px 2px rgba(3, 70, 148, 1)' : '0px 0px 5px 2px rgba(0, 118, 255, 0.15)' }} >
            <button
                onClick={onClick}
                className={`w-full flex flex-col gap-2 md:flex-row items-start md:items-center justify-between px-6 py-4 md:p-8 text-left text-gray-800 text-base md:text-[24px] font-medium focus:outline-none`}
            >
                <div className='flex flex-row items-center gap-4 flex-3'>
                    {/* <div className='w-[28px] md:w-[30px] pr-2 mr-3'> */}
                    {!isOpen ?
                        <img src="/asset/icon/plus.png" className="w-[18px]" />
                        :
                        <img src="/asset/icon/minus.png" className="w-[18px]" />
                    }
                    {/* </div> */}
                    <p className={`${isOpen ? 'text-[#034694]' : 'text-black'} capitalize`}>{title}</p>
                </div>

                <div className='flex flex-row flex-wrap gap-x-1 gap-y-2 items-center pl-[2rem] md:pl-0'>
                    {((mobileView && !isOpen) ? tags?.slice(0, 2) : tags)?.map((val, idx) => (
                        <div key={idx} className='py-1 px-2 rounded-xl md:rounded-full bg-[#E4F0FF]'>
                            <p className='text-[11.5px] md:text-xs'>{val}</p>
                        </div>
                    ))}
                    {(tags?.length > 2 && mobileView && !isOpen) &&
                        <div className='py-1 px-2 rounded-xl md:rounded-full bg-[#E4F0FF]'>
                            <p className='text-[11px] md:text-xs'>+{tags?.length - 2}</p>
                        </div>
                    }
                </div>
            </button>
            <div
                ref={contentRef}
                style={{ maxHeight: height }}
                className="overflow-hidden transition-all duration-300"
            >
                <div className="px-4 py-2 text-black bg-white rounded-b-[35px]">
                    <div className='pb-6'>{content}</div>
                </div>
            </div>
        </div>
    );
};

const Accordion = ({ items, isWithID, openIndex, setIsDrawerOpen, setDrawerTitle, setJo, jobID, is_loading }) => {
    const { startTransition } = useDrawerTransition();
    const { lang } = useSelector((state) => state.globalReducer);

    const dispatch = useDispatch();
    const isLogin = localStorage?.getItem('isLogin');
    const userToken = localStorage?.getItem('authToken') || '';

    const [activeIndex, setActiveIndex] = useState(null);
    const [isLoading, setisLoading] = useState(false);
    const isApplyingRef = useRef(false);

    useEffect(() => {
        if (openIndex) {
            setActiveIndex(0);
            toggle(0);
        } else {
            setActiveIndex(null);
            toggle(null);
        }
    }, [openIndex])

    const toggle = (index) => {
        setActiveIndex(index === activeIndex ? null : index);
    };


    const showSuccess = (message) => {
        Swal.fire({
            title: 'Success!',
            text: message || 'Action Successful!',
            icon: 'success',
            timer: 3000,
            showConfirmButton: false,
            customClass: {
                popup: 'my-custom-popup'
            }
        });
    };


    const showError = (error) => {
        Swal.fire({
            title: `${Lang(lang, 'warning')}!`,
            text: error || Lang(lang, 'apply_job_failed'),
            icon: 'warning',
            timer: 3000,
            showConfirmButton: false,
            customClass: {
                popup: 'my-custom-popup'
            }
        });
    };

    const showLoginInfo = (jobID) => {
        Swal.fire({
            title: Lang(lang, 'please_login'),
            text: Lang(lang, 'login_to_apply_desc'),
            icon: 'info',
            timer: 3000,
            showConfirmButton: false,
            customClass: {
                popup: 'my-custom-popup'
            }
        }).then(() => {
            startTransition(`/${lang}/login?from=portal`, { jobID });
        });
    };

    const submitApplication = async (job) => {
        if (isApplyingRef.current) return;

        isApplyingRef.current = true;
        setisLoading(true);

        try {
            if (!userToken) {
                isApplyingRef.current = false;
                setisLoading(false);
                dispatch(setIsLogin(false));
                clearSession()
                showLoginInfo(job?.jobID);
                return;
            }

            const profileCompletion = await loadCurrentApplicantCompletion({ userToken })

            if (profileCompletion !== 10) {
                isApplyingRef.current = false;
                setisLoading(false);
                showError(Lang(lang, 'profile_not_complete'))
                setTimeout(() => {
                    startTransition(`/${lang}/profile`, { jobID: job?.jobID });
                }, 3000);
                return;
            }

            const profileData = await loadCurrentApplicantProfile({ userToken })

            if (
                !profileData
                || !profileData.gender
                || !profileData.fullName
                || !profileData.email
                || !profileData.mobilePhoneNumber
            ) {
                isApplyingRef.current = false;
                setisLoading(false);
                showError(Lang(lang, 'profile_not_complete'))
                setTimeout(() => {
                    startTransition(`/${lang}/profile`, { jobID: job?.jobID });
                }, 3000);
                return;
            }

            const candidateData = {
                candidateName: profileData.fullName,
                email: profileData.email,
                phoneNumber: profileData.mobilePhoneNumber,
                resumeURL: profileData.resumeURL || '',
                remarks: '',
                jobID: job.jobID,
                status: 'Screening'
            };
            await addData({
                url: 'Candidates',
                params: candidateData,
                userToken,
            }).then(async res => {
                if (res?.data) {
                    setTimeout(() => {
                        isApplyingRef.current = false;
                        setisLoading(false)
                        showSuccess(Lang(lang, 'apply_job_success'))
                    }, 500);
                } else {
                    setTimeout(() => {
                        isApplyingRef.current = false;
                        setisLoading(false)
                        showError(res)
                    }, 500);
                }
            })
        } catch (error) {
            setTimeout(() => {
                isApplyingRef.current = false;
                setisLoading(false)
                showError(error)
            }, 500);
        }
    }

    return (
        <div className="w-full mx-auto divide-y divide-gray-200">
            {items.map((item, index) => (
                <AccordionItem
                    key={index}
                    title={`${item.title}`}
                    tags={Array.isArray(item?.tags) ? item?.tags : []}
                    content={item.content || (
                        <div >
                            <div className="pt-2 wysiwyg-editor" dangerouslySetInnerHTML={{ __html: lang === 'en' ? item.job.jobDescription : item.job.jobDescription_Id }} />
                            <div className="flex flex-col md:flex-row gap-3.5 justify-between w-full pt-6">
                                <button type="button" disabled={is_loading || isLoading} className={`${is_loading || isLoading ? 'cursor-not-allowed bg-[#FCA5A5] border-[#FCA5A5]' : 'cursor-pointer bg-[#EA2427] border-[#EA2427] hover:bg-white hover:text-[#EA2427]'} text-white border rounded-full py-2 px-6 font-semibold text-sm md:text-base`} onClick={() => {
                                    if (is_loading || isLoading) return;

                                    if (isLogin) {
                                        if (isSessionExpired()) {
                                            dispatch(setIsLogin(false));
                                            clearSession()
                                            showLoginInfo(item?.jobID);
                                        } else {
                                            submitApplication(item?.job)
                                        }

                                    } else {
                                        showLoginInfo(item?.jobID);

                                        // setIsDrawerOpen(true);
                                        // setDrawerTitle(item.job.jobTitle);
                                        // setJob({ ...item?.job, tags: `${item?.job?.location}, ${item?.job?.jobType}, ${item.job.tags}` })
                                    }
                                }}>{(is_loading || isLoading) ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto md:mx-[60px]" /> : Lang(lang, 'make_your_move')}</button>
                                <div className='flex flex-row gap-4 items-center justify-between h-[44px] px-[20px] py-[12px] bg-[#E4F0FF] border border-2 border-[#FFFFFF] rounded-full' style={{ boxShadow: '0px 0px 6px 2px rgb(0 118 255 / 15%)' }}>
                                    <p className={`text-black text-[15px]`}>{Lang(lang, 'share')}</p>
                                    <div className="flex flex-row gap-3">
                                        <a href='' onClick={(a) => {
                                            a.preventDefault();
                                            navigator.clipboard.writeText(`${window.location.origin}/${lang}/portal?id=${btoa(item.job.jobID)}`)
                                            showSuccess(Lang(lang, 'link_coppied'))
                                        }}>
                                            <img src="/asset/icon/copy.svg" className="w-[18px]" />
                                        </a>
                                        <a
                                            href={`https://wa.me/send?text=${window.location.origin}/${lang}/portal?id=${btoa(item.job.jobID)}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <img src="/asset/icon/whatsapp.svg" className="w-[18px]" alt="Share on WhatsApp" />
                                        </a>

                                    </div>
                                </div>
                            </div>
                        </div>)}
                    isOpen={index === activeIndex}
                    onClick={() => { toggle(index) }}
                    id={isWithID ? `${item?.jobID}` : ''}
                />
            ))}
        </div>
    );
};

export default Accordion;
